/**
 * ContractEditor — Editor de contratos con @react-pdf/renderer
 *
 * Dependencias:
 *   npm install @react-pdf/renderer
 *
 * Uso:
 *   import ContractEditor from "./ContractEditor";
 *
 *   <ContractEditor
 *     template="Contrato entre [nombre_cliente] y [nombre_prestador]..."
 *     data={{ nombre_cliente: "ACME S.A.", nombre_prestador: "Juan Pérez" }}
 *     documentTitle="contrato-servicios"
 *   />
 */

import { useState, useEffect, useCallback } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  PDFDownloadLink,
  pdf,
} from "@react-pdf/renderer";
import { theme } from "antd";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PdfEditorProps {
  /** Plantilla con variables en formato [nombre_variable] */
  template?: string;
  /** JSON con los valores de los campos */
  data?: any;
  /** Nombre del archivo para descargar */
  documentTitle?: string;
  /** Lista de campos a editar (no requerido en modo readOnly) */
  fieldsForm?: React.ReactNode;
  /** Se invoca cuando el usuario edita el texto de la plantilla, para que el padre pueda persistirlo. */
  onTemplateChange?: (template: string) => void;
  /** Tipo de documento */
  typeDocument?: 'Contrato' | 'Factura' | 'Cotización';
  /**
   * Solo lectura: oculta el panel de edición (plantilla + campos) y muestra
   * únicamente la vista previa del PDF junto al botón de descarga.
   */
  readOnly?: boolean;
  /**
   * Documento PDF personalizado (@react-pdf/renderer). Cuando se proporciona,
   * reemplaza al documento generado desde `template` + `data`.
   */
  document?: React.ReactElement;
}

interface Segment {
  text: string;
  isVariable: boolean;
  key?: string;
  filled: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractKeys(template: string): string[] {
  const regex = /\[([^\]]+)\]/g;
  const keys = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = regex.exec(template)) !== null) keys.add(m[1]);
  return [...keys];
}

const HTML_ENTITIES: Record<string, string> = {
  "&quot;": '"',
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

function decodeHtmlEntities(text: string): string {
  if (!text || text.indexOf("&") === -1) return text;
  return text.replace(
    /&quot;|&amp;|&lt;|&gt;|&#39;|&apos;|&nbsp;/g,
    (match) => HTML_ENTITIES[match]
  );
}

// ─── PDF Styles (@react-pdf/renderer) ────────────────────────────────────────

const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 72,
    paddingBottom: 72,
    paddingHorizontal: 80,
    fontFamily: "Times-Roman",
    fontSize: 11,
    lineHeight: 1.8,
    color: "#1a1a1a",
  },
  lineRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  plain: {
    fontFamily: "Times-Roman",
  },
  filledVar: {
    fontFamily: "Times-Bold",
    color: "#92400e",
    backgroundColor: "#fef3c7",
  },
  emptyVar: {
    fontFamily: "Times-Roman",
    color: "#991b1b",
    backgroundColor: "#fee2e2",
  },
});

// ─── PDF Document ─────────────────────────────────────────────────────────────

interface ContractDocumentProps {
  template: string;
  fields: Record<string, string>;
}

function ContractDocument({ template, fields }: ContractDocumentProps) {
  const lines = decodeHtmlEntities(template).split("\n");
  const lineSegments = lines.map((line) => {
    if (!line) {
      return [{ text: "\u00A0", isVariable: false, filled: true }];
    }
    const parts = line.split(/(\[[^\]]+\])/g);
    return parts.map((part): Segment => {
      const varMatch = part.match(/^\[([^\]]+)\]$/);
      if (varMatch) {
        const key = varMatch[1];
        const value = decodeHtmlEntities(fields[key] || "");
        return {
          text: value?.trim() ? value : `[${key}]`,
          isVariable: true,
          key,
          filled: !!value?.trim(),
        };
      }
      return {
        text: part.replace(/ {2,}/g, (match) => "\u00A0".repeat(match.length)),
        isVariable: false,
        filled: true
      };
    });
  });


  return (
    <Document>
      <Page size="LETTER" style={pdfStyles.page}>
        {lineSegments.map((lineParts, lineIdx) => (
          <View key={lineIdx} style={pdfStyles.lineRow}>
            {lineParts.map((seg, segIdx) =>
              seg.isVariable ? (
                <Text
                  key={segIdx}
                  style={seg.filled ? pdfStyles.filledVar : pdfStyles.emptyVar}
                >
                  {seg.text}
                </Text>
              ) : (
                <Text key={segIdx} style={pdfStyles.plain}>
                  {seg.text}
                </Text>
              )
            )}
          </View>
        ))}
      </Page>
    </Document>
  );
}

// ─── Default values ───────────────────────────────────────────────────────────

const DEFAULT_TEMPLATE = `                      CONTRATO DE PRESTACIÓN DE SERVICIOS PROFESIONALES

En la ciudad de [direcion_lugar], a los [fecha_dia] días del mes de [fecha_mes] de [fecha_anio], comparecen por una parte [nombre_cliente], a quien en lo sucesivo se le denominará como "EL CLIENTE", y por la otra parte [nombre_prestador], a quien en lo sucesivo se le denominará como "EL PRESTADOR", bajo las siguientes declaraciones y cláusulas:

                                                       DECLARACIONES

I. EL CLIENTE declara:
A. Ser una persona [cliente_rfc] legalmente constituida con capacidad legal para contratar y obligarse.
B. Tener su domicilio ubicado en: [domicilio_cliente].
C. Requerir los servicios profesionales de "EL PRESTADOR" en el área de [area_especialidad].

II. EL PRESTADOR declara:
A. Ser una persona física con plena capacidad de ejercicio, contando con la cédula profesional número [cedula_profesional] emitida por la SEP.
B. Tener su domicilio ubicado en: [domicilio_prestador].

                                                       CLÁUSULAS

PRIMERA. OBJETO DEL CONTRATO. "EL PRESTADOR" se obliga a realizar a favor de "EL CLIENTE" los siguientes servicios:
1. [descripcion_servicio_a].
2. [descripcion_servicio_b].
3. [entregables_especificos].

SEGUNDA. HONORARIOS. Como contraprestación por los servicios recibidos, "EL CLIENTE" pagará a "EL PRESTADOR" la cantidad total de $[monto] ([monto_letras] Pesos 00/100 M.N.).`;

const DEFAULT_DATA: Record<string, string> = {
  direcion_lugar: "Ciudad de México",
  fecha_dia: "15",
  fecha_mes: "Mayo",
  fecha_anio: "2025",
  nombre_cliente: "Empresa ABC S.A. de C.V.",
  nombre_prestador: "Juan Pérez González",
  cliente_rfc: "ABC123456XYZ",
  domicilio_cliente: "Av. Insurgentes Sur 1234, Col. Del Valle, CDMX",
  area_especialidad: "Desarrollo de Software",
  cedula_profesional: "12345678",
  domicilio_prestador: "Calle Reforma 567, Col. Centro, CDMX",
  descripcion_servicio_a: "Desarrollo de plataforma web personalizada",
  descripcion_servicio_b: "Capacitación al equipo técnico",
  entregables_especificos: "Código fuente, documentación y manuales de uso",
  monto: "50,000.00",
  monto_letras: "Cincuenta Mil",
};

// ─── Email Modal ──────────────────────────────────────────────────────────────

function EmailModal({
  isOpen,
  onClose,
  content,
}: {
  isOpen: boolean;
  onClose: () => void;
  content: string;
}) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("Contrato de Prestación de Servicios");
  const { token } = theme.useToken();

  if (!isOpen) return null;

  const handleSend = () => {
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(content)}`;
    onClose();
  };

  return (
    <div
      style={{
        ...ui.overlay,
        background: "rgba(0,0,0,0.45)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...ui.modal,
          background: token.colorBgContainer,
          boxShadow: token.boxShadowSecondary,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <p style={{ ...ui.modalTitle, color: token.colorText }}>Enviar por correo</p>
        <input
          style={{
            ...ui.modalInput,
            background: token.colorBgContainer,
            color: token.colorText,
            borderColor: token.colorBorder,
          }}
          type="email"
          placeholder="destinatario@correo.com"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <input
          style={{
            ...ui.modalInput,
            background: token.colorBgContainer,
            color: token.colorText,
            borderColor: token.colorBorder,
          }}
          type="text"
          placeholder="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            style={{
              ...ui.btn,
              background: token.colorBgContainer,
              color: token.colorText,
              borderColor: token.colorBorder,
            }}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            style={{
              ...ui.btn,
              background: token.colorPrimary,
              color: "#fff",
              borderColor: "transparent",
            }}
            onClick={handleSend}
          >
            Enviar ↗
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PdfEditor({
  template: initialTemplate = DEFAULT_TEMPLATE,
  data: initialData = DEFAULT_DATA,
  documentTitle = "contrato",
  fieldsForm,
  onTemplateChange,
  typeDocument = "Contrato",
  readOnly = false,
  document: customDocument,
}: PdfEditorProps) {
  const { token } = theme.useToken();
  const [template, setTemplate] = useState(initialTemplate);
  const [fields, setFields] = useState<Record<string, string>>(initialData);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sharing, setSharing] = useState(false);



  const detectedKeys = extractKeys(template);
  const filledCount = detectedKeys.filter((k) => fields[k]?.trim()).length;
  const totalCount = detectedKeys.length;
  const fillPct =
    totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 100;
  const progressColor =
    fillPct === 100 ? token.colorSuccess : fillPct > 50 ? token.colorWarning : token.colorError;


  const plainText = useCallback((): string => {
    let result = decodeHtmlEntities(template);
    detectedKeys.forEach((key) => {
      result = result.split(`[${key}]`).join(decodeHtmlEntities(fields[key]) || `[${key}]`);
    });
    return result;
  }, [template, fields, detectedKeys, initialData]);

  // Comparte el PDF generado como archivo real vía Web Share API. El
  // destinatario y la app los elige el usuario en la hoja nativa del sistema.
  // Nota: WhatsApp ignora cualquier texto cuando recibe un adjunto, por eso
  // solo se comparte el archivo. Si no hay Web Share de archivos (desktop), se
  // descarga el PDF para adjuntarlo manualmente.
  const handleShare = async () => {
    setSharing(true);
    try {
      const blob = await pdf(pdfDoc).toBlob();
      const file = new File([blob], `${documentTitle}.pdf`, { type: "application/pdf" });

      const canShareFile =
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] });

      if (canShareFile) {
        await navigator.share({ files: [file], title: documentTitle });
        return;
      }

      // Respaldo (desktop sin Web Share de archivos): descarga el PDF.
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `${documentTitle}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // El usuario canceló el diálogo nativo de compartir → no es un error.
      if (err?.name !== "AbortError") {
        // Silencioso: no interrumpimos el flujo del usuario.
        console.error("No se pudo compartir el documento", err);
      }
    } finally {
      setSharing(false);
    }
  };
  const pdfDoc = customDocument ?? <ContractDocument template={template} fields={{ ...fields }} />;


  // Synchronize fields with initialData whenever it changes
  useEffect(() => {
    setFields((prev) => {
      const next = { ...prev };
      detectedKeys.forEach((k) => {
        // If initialData provides a value for this key, use it. Otherwise keep existing or set empty.
        if (initialData && initialData[k] !== undefined) {
          next[k] = initialData[k] ?? "";
        } else if (!(k in next)) {
          next[k] = "";
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, initialData]);

  // Sync template state when prop changes
  useEffect(() => {
    if (initialTemplate) {
      setTemplate(initialTemplate);
    }
  }, [initialTemplate]);
  return (
    <>
      <style>{`
        *{box-sizing:border-box}
        .fi:focus{outline:none;border-color:${token.colorPrimary}!important;box-shadow:0 0 0 3px ${token.colorPrimaryBg}}
        .bh:hover{background:${token.colorFillTertiary}!important}
        @keyframes lp{0%,100%{opacity:1}50%{opacity:.3}}
      `}</style>

      <div
        style={{
          ...ui.root,
          background: token.colorBgLayout,
          color: token.colorText,
          ...(readOnly ? { flex: 1, minHeight: 0, height: "100%" } : {}),
        }}
      >
        {/* ── Header ── */}
        <header
          style={{
            ...ui.header,
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div style={{ ...ui.headerTitle, color: token.colorText }}>
            <DocIcon />
            Editor de {typeDocument}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="bh"
              style={{
                ...ui.btn,
                background: token.colorBgContainer,
                color: token.colorText,
                borderColor: token.colorBorder,
              }}
              onClick={handleShare}
              disabled={sharing}
            >
              <ShareIcon /> {sharing ? "Generando…" : "Compartir"}
            </button>
            {!readOnly && (
              <button
                className="bh"
                style={{
                  ...ui.btn,
                  background: token.colorBgContainer,
                  color: token.colorText,
                  borderColor: token.colorBorder,
                }}
                onClick={() => setEmailOpen(true)}
              >
                <MailIcon /> Correo
              </button>
            )}
            {/* PDFDownloadLink genera el blob PDF real en el cliente */}
            <PDFDownloadLink
              document={pdfDoc}
              fileName={`${documentTitle}.pdf`}
              style={{ textDecoration: "none" }}
            >
              {({ loading }) => (
                <button
                  style={{
                    ...ui.btn,
                    background: token.colorPrimary,
                    color: "#fff",
                    borderColor: "transparent",
                    fontWeight: 600,
                  }}
                >
                  <DlIcon />
                  {loading ? "Generando…" : "Descargar PDF"}
                </button>
              )}
            </PDFDownloadLink>
          </div>
        </header>

        {/* ── Body ── */}
        <div style={ui.body}>
          {/* Left: template + fields (oculto en modo solo lectura) */}
          {!readOnly && (
          <aside
            style={{
              ...ui.left,
              background: token.colorBgContainer,
              borderRight: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            {/* Template */}
            <div
              style={{
                ...ui.section,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <div style={{ ...ui.secTitle, color: token.colorTextSecondary }}>Plantilla</div>
              <textarea
                style={{
                  ...ui.tmplArea,
                  background: token.colorBgLayout,
                  color: token.colorText,
                  borderColor: token.colorBorder,
                }}
                value={decodeHtmlEntities(template)}
                onChange={(e) => {
                  setTemplate(e.target.value);
                  onTemplateChange?.(e.target.value);
                }}
                rows={7}
                placeholder="Escribe tu plantilla con [variables]..."
              />
            </div>

            {/* Fields */}
            <div
              style={{
                ...ui.section,
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                borderBottom: "none",
              }}
            >
              <div style={{ ...ui.secTitle, color: token.colorTextSecondary }}>
                Campos detectados
                <span
                  style={{
                    ...ui.pill,
                    background: token.colorFillSecondary,
                    color: token.colorText,
                  }}
                >
                  {detectedKeys.length}
                </span>
              </div>

              <div style={ui.fieldScroll}>{fieldsForm}</div>
            </div>
          </aside>
          )}

          {/* Right: PDFViewer */}
          <section style={ui.right}>
            <div
              style={{
                ...ui.previewBar,
                background: token.colorBgContainer,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: token.colorTextSecondary,
                }}
              >
                <span style={{ ...ui.liveDot, background: token.colorSuccess }} />
                {readOnly ? "Vista previa del documento" : "Vista previa PDF en tiempo real"}
              </div>
              {!readOnly && (
                <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                  {filledCount}/{totalCount} campos
                </span>
              )}
            </div>

            {/* PDFViewer renders the actual PDF inline — no iframe hacks needed */}
            <PDFViewer style={{ flex: 1, border: "none" }} showToolbar={false}>
              {pdfDoc}
            </PDFViewer>

            {/* Progress bar (oculta en modo solo lectura) */}
            {!readOnly && (
            <div
              style={{
                ...ui.statusBar,
                background: token.colorBgContainer,
                borderTop: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                {fillPct === 100
                  ? "Documento completo ✓"
                  : `${totalCount - filledCount} campos por llenar`}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 120,
                    height: 4,
                    background: token.colorFillSecondary,
                    borderRadius: 2,
                  }}
                >
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      width: `${fillPct}%`,
                      background: progressColor,
                      transition: "width .3s, background .3s",
                    }}
                  />
                </div>
                <span style={{ fontSize: 12, color: token.colorTextSecondary, minWidth: 30 }}>
                  {fillPct}%
                </span>
              </div>
            </div>
            )}
          </section>
        </div>
      </div>

      <EmailModal
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        content={plainText()}
      />

    </>
  );
}

// ─── UI styles ────────────────────────────────────────────────────────────────

const ui: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    // height: "88vh",
    fontFamily: "system-ui,-apple-system,sans-serif",
  },
  header: {
    padding: "11px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
  },
  badge: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 6,
    fontWeight: 600,
  },
  btn: {
    padding: "7px 13px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "inherit",
  },
  btnPrimary: {
    borderColor: "transparent",
  },
  body: { display: "flex", flex: 1, overflow: "hidden" },
  left: {
    width: 360,
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    overflow: "hidden",
  },
  section: { padding: "14px 16px" },
  secTitle: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  pill: {
    fontSize: 11,
    padding: "1px 7px",
    borderRadius: 10,
    fontWeight: 600,
  },
  tmplArea: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 11,
    fontFamily: "monospace",
    resize: "vertical",
    lineHeight: 1.6,
  },
  fieldScroll: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingRight: 2,
  },
  right: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  previewBar: {
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#22c55e",
    display: "inline-block",
    animation: "lp 2s infinite",
  },
  statusBar: {
    padding: "8px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
  },
  modal: {
    borderRadius: 12,
    padding: 24,
    width: 360,
  },
  modalTitle: { fontSize: 15, fontWeight: 600, marginBottom: 16 },
  modalInput: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 12,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function DocIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}
function DlIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,12 2,6" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}